import React, { useCallback, useEffect, useState } from 'react';
import FeedbackDashboardFilterSelect from '../FeedbackDashboardFilterSelect';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { Button, FormGroup } from '@mui/material';
import { FeedbackFilters } from '../FeedbackDashboardFilterBar';
import FeedbackFilterCheckbox from '../FeedbackFilterCheckbox';
import SearchField from './SearchField';

const PAGE_COUNT = 30;
const getRows = (
  filter: FeedbackFilters,
  term: string,
  handleChange: (id: string, _name: string, selected: boolean) => void,
): { rows: React.ReactNode; isAnySelected: boolean } => {
  let isAnySelected = false;
  const rows = Object.keys(filter).map(d => {
    const f = filter[d];
    if (!f.name.toLocaleLowerCase().includes(term)) {
      return null;
    }
    f.selected && (isAnySelected = f.selected);
    return (
      <FeedbackFilterCheckbox
        key={d}
        id={d}
        filter={f}
        handleChange={handleChange}
      />
    );
  });
  return { rows, isAnySelected };
};

export type FeedbackSingleFilterProps<T extends string> = {
  filters: FeedbackFilters;
  applyFilter: (key: T) => unknown;
  onClear: () => unknown;
};

export type FeedbackFilterProps = {
  filters: FeedbackFilters;
  applyFilter: (selected: FeedbackFilters) => unknown;
  onRemove: (key: string) => unknown;
  onClear: () => unknown;
};

interface FeedbackFilterDropdownProps {
  feedbackFilter: FeedbackFilterProps;
  icon: IconDefinition;
  label: string;
  showSearchBox: boolean;
  className?: string;
  range?: boolean;
}

const FeedbackFilterDropdown: React.FC<FeedbackFilterDropdownProps> = ({
  feedbackFilter,
  icon,
  label,
  showSearchBox,
  className,
  range,
}) => {
  const [filter, setFilter] = useState<FeedbackFilters>(feedbackFilter.filters);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [value, setValue] = useState<number[]>([2015, 2026]);

  useEffect(() => {
    setFilter(feedbackFilter.filters);
  }, [feedbackFilter.filters, setFilter]);

  const handleClose = useCallback(() => {
    feedbackFilter.applyFilter(filter);
  }, [filter, feedbackFilter.applyFilter]);
  const handleChange = useCallback(
    (id: string, _name: string, selected: boolean) => {
      setFilter(s => ({
        ...s,
        [id]: {
          ...s[id],
          selected,
        },
      }));
    },
    [setFilter],
  );

  const term = searchTerm.toLocaleLowerCase();
  const { rows, isAnySelected } = getRows(filter, term, handleChange);
  const rowsArr = React.Children.toArray(rows);
  const slicedRows = rowsArr.slice(0, page * PAGE_COUNT);
  return (
    <FeedbackDashboardFilterSelect
      type="select"
      selectorLabel={label}
      icon={icon}
      disableClearButton={!isAnySelected}
      onClearAll={feedbackFilter.onClear}
      onClose={handleClose}
      className={className}
      Component={undefined}
    >
      {showSearchBox && (
        <div className="search__wrapper">
          <SearchField
            value={searchTerm}
            size="small"
            onChange={setSearchTerm}
            placeholder="Search"
          />
        </div>
      )}
      <FormGroup>{slicedRows}</FormGroup>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button onClick={() => setPage(page + 1)} disabled={rowsArr.length <= page * PAGE_COUNT}>
          Load More
        </Button>
      </div>

    </FeedbackDashboardFilterSelect>
  );
};

export default FeedbackFilterDropdown;
